import { all, takeEvery, fork, put, select, call, cancelled, takeLatest } from 'redux-saga/effects';
import isEmpty from 'lodash.isempty';
import { AnyAction } from 'redux';

import { mySubjectsActionsInterfaces, mySubjectsActions } from '.';
import { staticViewsActions, staticViewsSelectors } from '..';
import { executeErrorCode } from '../../auth/actions';
import { mySubjectsActionTypes } from '.';
import {
  CREATE_FINAL_MARKS_SUCCESS,
  UPDATE_FINAL_MARKS_SUCCESS,
  CREATE_FINAL_MARK_SUCCESS,
  UPDATE_FINAL_MARK_SUCCESS,
  DELETE_FINAL_MARK_SUCCESS,
} from '../../marks/types';
import { STUDENTS_COPY_FINAL_MARKS_SUCCESS } from '../../dynamicTabs/stronaPrzedmiotu/types';
import { PROTOCOL_GET_STATUS_SUCCESS } from '../../appConfig/types';
import MySubjectsService from '../../../services/mySubjects';

import UrlBuilder from '../../../helpers/urlBuilder';
import DataDestructuring from '../../../helpers/DataDestructuring';

const mandatoryColumns = [
  'przedmiotId',
  'przedmiotNazwa',
  'semestrAkademicki',
  'rokAkademicki',
  'wydzialZlecajacyKod',
  'formyZajec',
  'typPrzydzialu',
  'kierownicyKartyLista',
  'wagiFormyZajec',
  'wydzialZlecajacyNazwa',
  'wydzialZlecajacyKod',
];
const applicableView = 'MySubjects';

export function* initMySubjectsWatcher(): Generator {
  yield takeEvery(mySubjectsActionTypes.INIT, initMySubjects);
}

export function* initMySubjects(action: mySubjectsActionsInterfaces.Init): Generator {
  const { view } = action.payload;
  const { loading, data, userColumnsSetup } = (yield select(state =>
    staticViewsSelectors.getStaticViewState(state, view),
  )) as ViewDataSet;
  if (!loading && !data.length) {
    try {
      /*
        czekamy, aż wykonają się sagi pobierające config filtrów i kolumn
        - kolumny pobieramy tylko, gdy userColumnsSetup jest pusty - unikamy niepotrzebnego requestu
        - call - zgodnie z dokumentacją jako argumentu nie można podać obiektu dispatchowanej akcji.
          W tym przypadku podawany jest generator a kolejne argumenty to argumenty przekazywane do tego generatora.
          Oznacza to, że nie jest dispatchowana akcja w redux
      */
      yield all([
        put(mySubjectsActions.getFiltersList(view)),
        call(getMySubjectsFiltersList, mySubjectsActions.getFiltersList(view)),
        !userColumnsSetup.details.length && !userColumnsSetup.main.length
          ? call(getColumnsConfig, mySubjectsActions.getColumnsConfig(view))
          : null,
      ]);
      yield put(mySubjectsActions.getData(view));
      yield put(staticViewsActions.initFinish(view));
    } catch (error) {
      if (error && error.errorCode) {
        yield put(executeErrorCode(error.errorCode, action, error));
      }
    }
  }
}
export function* getMySubjectsWatcher(): Generator {
  yield takeLatest(mySubjectsActionTypes.GET_DATA_START, getMySubjects);
}
/**
 * pobieranie danych do tabeli "Moje Przedmioty"
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getMySubjects(action: mySubjectsActionsInterfaces.GetData): Generator {
  const { view } = action.payload;
  const abortController = new AbortController();
  try {
    const { filters, sorting, userColumnsSetup, page } = (yield select(state =>
      staticViewsSelectors.getStaticViewState(state, view),
    )) as ViewDataSet;
    const columns = [...userColumnsSetup.main];
    mandatoryColumns.forEach(key => {
      if (!columns.includes(key)) {
        columns.push(key);
      }
    });
    const requestAtributes = {
      filter: filters.allFilters.map((filters: any) => ({ name: filters.name, values: filters.selected })),
      page: page.toString(),
      columns,
      sort: sorting.order ? (sorting.order === 'ascend' ? 'asc' : 'desc') : null,
      orderBy: sorting.columnName,
    };
    let response: any = yield call(MySubjectsService.getMySubjects, requestAtributes, abortController.signal);
    /* W momencie kiedy zapytanie zwróci pustą tablicę/obiekt bądź nie będzie zwierało poprawnej daty wykona się zapytanie z no cache */
    if (!response.przedmiotPage.data || isEmpty(response.przedmiotPage.data)) {
      response = yield call(MySubjectsService.getMySubjects, requestAtributes, abortController.signal, true);
    }
    /* Aby poprawnie zmapować dane w reducer, payload powinien być zbudowany wg interfejsu GetDataSuccessPayload */
    const payload: GetDataSuccessPayload = {
      ...response.przedmiotPage,
      cacheDuration: response.cacheTime,
      controlSum: response.controlSum === 'filters' ? '' : response.controlSum,
      data: response.przedmiotPage.data.map((obj: any) => ({
        ...obj,
        key: `${obj.przedmiotId}_${obj.wydzialZlecajacyKod}`,
        kodPrzedmiotuBezSpacji: obj.przedmiotKod.replaceAll(' ', ''),
      })),
    };
    yield put(staticViewsActions.getDataSuccess(view, payload));
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
    yield put(staticViewsActions.getDataFail(view));
  } finally {
    if (yield cancelled()) {
      abortController.abort();
    }
  }
}
export function* refreshFilterValuesWatcher(): Generator {
  yield takeLatest(mySubjectsActionTypes.FILTERS_REFRESH_VALUES_START, refreshFilterValues);
}
/**
 * aktualizacja wartości filtrów
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* refreshFilterValues(action: mySubjectsActionsInterfaces.RefreshFilterValues): Generator {
  const { view } = action.payload;
  const abortController = new AbortController();
  try {
    const { filters } = (yield select(state => staticViewsSelectors.getStaticViewState(state, view))) as ViewDataSet;
    /* budowa parametrów do prośby o aktualizacje listy filtrów */
    const requestAttributes: FilterValuePayload = {
      filter: filters.allFilters
        .filter(filter => filter.active)
        .reduce((acc: object, filter) => ({ ...acc, [filter.name]: DataDestructuring.returnMultiFilterData(filter) }), {}),
    };
    const response: any = yield call(MySubjectsService.refreshFilterValues, requestAttributes, abortController.signal);
    yield put(staticViewsActions.refreshFilterValueSuccess(view, response.response));
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
    yield put(staticViewsActions.refreshFilterValueFail(view));
  } finally {
    if (yield cancelled()) {
      abortController.abort();
    }
  }
}

/*
  Poniższy generator nie ma watchera, ponieważ nie jest wywoływany z zewnątrz (np. z komponentu),
  a poprzez call w generatorze initMySubjects. Call nie dispatchuje akcji w redux.
*/
export function* getMySubjectsFiltersList(action: mySubjectsActionsInterfaces.GetFiltersList): Generator {
  const { view } = action.payload;
  try {
    const response: any = yield call(MySubjectsService.getMySubjectsFilterList);
    if (response) {
      yield put(staticViewsActions.getFiltersConfigSuccess(view, response.fieldFilter));
      const isDefault: object | undefined = response.fieldFilter.find((item: any) => item.isDefault);
      if (isDefault) {
        yield put(mySubjectsActions.refreshFilterValues(view));
      }
    }
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
    yield put(staticViewsActions.getFiltersConfigFail(view));
  }
}

export function* saveColumnsUserSetupWatcher(): Generator {
  yield takeEvery(mySubjectsActionTypes.COLUMNS_SAVE_USER_SETUP_START, saveColumnsUserSetup);
}
/**
 * ustawienie konfiguracji kolumn dla użytkownika
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* saveColumnsUserSetup(action: mySubjectsActionsInterfaces.SaveColumnsUserSetup): Generator {
  const { view, selectedColumns } = action.payload;
  try {
    yield call(MySubjectsService.saveColumnsUserSetup, { columnsSetup: selectedColumns });
    // yield put(mySubjectsActions.getColumnsConfig(view));
    yield call(getColumnsConfig, mySubjectsActions.getColumnsConfig(view));
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}

/**
 * Pobieranie konfiguracji kolumn dla tabeli "Moje przedmioty".
 * Poniższy generator nie ma watchera, ponieważ nie jest wywoływany z zewnątrz (np. z komponentu),
 * a poprzez call w generatorze initMySubjects. Call nie dispatchuje akcji w redux.
 * Z tego powodu pierwszym etapem jest dispatch akcji MY_SUBJECTS/COLUMNS_GET_CONFIG_START, która zmieni stan loading w redux.
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */

export function* getColumnsConfig(action: mySubjectsActionsInterfaces.GetColumnsConfig): Generator {
  const { view } = action.payload;
  try {
    /* Dispatch akcji MY_SUBJECTS/COLUMNS_GET_CONFIG_START  */
    yield put(mySubjectsActions.getColumnsConfig(view));
    const response: any = yield MySubjectsService.getMySubjectsColumnsData();
    yield put(staticViewsActions.getColumnsConfigSuccess(view, response.przedmiotField));
    yield put(staticViewsActions.saveColumnsUserSetupSuccess(view, response.przedmiotField));
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
    yield put(staticViewsActions.getColumnsConfigFail(view));
  }
}

export function* refreshMySubjectDataWatcher(): Generator {
  yield takeEvery(
    [
      mySubjectsActionTypes.FILTERS_REFRESH_VALUES_START,
      // staticViewsActionTypes.COLUMNS_GET_CONFIG_SUCCESS,
      mySubjectsActionTypes.REFRESH_DATA_START,
    ],
    refreshMySubjectData,
  );
}
/**
 * odświeżenie listy
 *
 * @export
 * @return {*}  {Generator}
 *
 */
export function* refreshMySubjectData(action: mySubjectsActionsInterfaces.RefreshData): Generator {
  const { view } = action.payload;
  const { initTable, selected } = (yield select(state => staticViewsSelectors.getStaticViewState(state, view))) as ViewDataSet;
  if (view === applicableView && !initTable) {
    // yield put(staticViewsActions.setLoading(action.payload.view));
    // yield call(getMySubjects, mySubjectsActions.getData(action.payload.view));
    yield put(mySubjectsActions.getData(action.payload.view));
    if (selected.length === 1) {
      yield put(mySubjectsActions.getRecordDetails(view, selected[0]));
    }
  }
}

export function* getMySubjectsGetDetailsWatcher(): Generator {
  yield takeEvery(mySubjectsActionTypes.RECORD_GET_DETAILS_START, getMySubjectsGetDetails);
}
/**
 * pobieranie szczegółów wybranego rekordu
 *
 * @export
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* getMySubjectsGetDetails(action: mySubjectsActionsInterfaces.GetRecordDetails): Generator {
  const { view, id } = action.payload;
  try {
    const { data } = (yield select(state => staticViewsSelectors.getStaticViewState(state, view))) as ViewDataSet;
    const [przedmiotId, wydzialZlecajacyKod] = id.toString().split('_');
    const selectedRow = data.find(row => row.przedmiotId == przedmiotId);
    if ((selectedRow && !selectedRow.details) || (selectedRow && selectedRow.details.length === 0)) {
      const { filters, userColumnsSetup } = (yield select(state =>
        staticViewsSelectors.getStaticViewState(state, view),
      )) as ViewDataSet;
      const columns = [...userColumnsSetup.details];
      mandatoryColumns.forEach(key => {
        if (!columns.includes(key)) {
          columns.push(key);
        }
      });
      const requestParams = {
        filter: [
          ...filters.allFilters.map((filter: any) => ({ name: filter.name, values: filter.selected })),
          ...UrlBuilder.generateFiltersArray({ wydzialZlecajacyKod }),
        ],
        id: Number(przedmiotId),
        columns,
      };
      const response: any = yield call(MySubjectsService.getMySubjectDetails, requestParams);
      yield put(staticViewsActions.getRecordDetailsSuccess(view, response.przedmiot, id));
    } else {
      yield put(staticViewsActions.setDetailsLoading(view));
    }
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
    yield put(staticViewsActions.getRecordDetailsFail(view));
  }
}
export function* syncDataWithCacheWatcher(): Generator {
  yield takeLatest(mySubjectsActionTypes.SYNC_DATA_WITH_CACHE_START, syncDataWithCache);
}
/**
 * Synchronizacja danych z cache dla "Moje Przedmioty"
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* syncDataWithCache(action: mySubjectsActionsInterfaces.SyncDataWithCache): Generator {
  const { view } = action.payload;
  const abortController = new AbortController();
  try {
    const { filters, sorting, userColumnsSetup } = (yield select(state =>
      staticViewsSelectors.getStaticViewState(state, view),
    )) as ViewDataSet;
    const columns = [...userColumnsSetup.main];
    mandatoryColumns.forEach(key => {
      if (!columns.includes(key)) {
        columns.push(key);
      }
    });
    const requestAtributes = {
      filter: filters.allFilters.map((filters: any) => ({ name: filters.name, values: filters.selected })),
      page: '1',
      columns,
      sort: sorting.order ? (sorting.order === 'ascend' ? 'asc' : 'desc') : null,
      orderBy: sorting.columnName,
      noCache: true.toString(),
    };

    if (!isEmpty(requestAtributes.filter)) {
      const response: any = yield call(MySubjectsService.getMySubjects, requestAtributes, abortController.signal);
      const { controlSum } = (yield select(state => staticViewsSelectors.getStaticViewCashe(state, view))) as WithCache;
      yield put(
        staticViewsActions.syncDataWithCacheSuccess(view, {
          controlSum: response.controlSum === 'filters' ? '' : response.controlSum,
          isUpToDate: controlSum === response.controlSum || response.controlSum === 'filters',
          duration: response.cacheTime,
        }),
      );
    }
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
    yield put(staticViewsActions.syncDataWithCacheFail(view));
  } finally {
    if (yield cancelled()) {
      abortController.abort();
    }
  }
}

export function* changeFinalMarkWatcher(): Generator {
  yield takeEvery(
    [
      CREATE_FINAL_MARKS_SUCCESS,
      UPDATE_FINAL_MARKS_SUCCESS,
      CREATE_FINAL_MARK_SUCCESS,
      UPDATE_FINAL_MARK_SUCCESS,
      DELETE_FINAL_MARK_SUCCESS,
      STUDENTS_COPY_FINAL_MARKS_SUCCESS,
      PROTOCOL_GET_STATUS_SUCCESS,
    ],
    changeFinalMark,
  );
}
/**
 *  funkcja odświeżająca listę przedmiotów w zakładce 'Moje przedmioty' po akcji związanej z oceną końcową
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* changeFinalMark(action: AnyAction): Generator {
  try {
    const { filters } = (yield select(state => staticViewsSelectors.getStaticViewState(state, applicableView))) as ViewDataSet;
    if (isEmpty(filters.allFilters)) {
      yield put(mySubjectsActions.init(applicableView));
    } else yield put(mySubjectsActions.getData(applicableView));
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}

export default function* rootSaga(): Generator {
  yield all([
    fork(initMySubjectsWatcher),
    fork(getMySubjectsWatcher),
    fork(refreshMySubjectDataWatcher),
    fork(refreshFilterValuesWatcher),
    fork(saveColumnsUserSetupWatcher),
    fork(getMySubjectsGetDetailsWatcher),
    fork(syncDataWithCacheWatcher),
    fork(changeFinalMarkWatcher),
  ]);
}
